// src/components/homepage/HomePageDifference.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Cpu, Bot, ShieldCheck, Rocket } from 'lucide-react';
import {ContentCard} from '@dopomogai/ui';

// Interfaces remain the same
interface DifferencePoint {
  title: string;
  text: string;
}

interface DifferenceData {
  headline: string;
  point1: DifferencePoint;
  point2: DifferencePoint;
  point3: DifferencePoint;
  point4: DifferencePoint;
}

interface HomePageDifferenceProps {
  differenceData: DifferenceData;
  id?: string;
}

export const HomePageDifference: React.FC<HomePageDifferenceProps> = ({ differenceData, id }) => {
  const differencePoints = [
    { icon: <Cpu size={32} strokeWidth={1.5} className="text-teal-500" />, data: differenceData.point1, accentColor: 'teal' },
    { icon: <Bot size={32} strokeWidth={1.5} className="text-sky-500" />, data: differenceData.point2, accentColor: 'sky' },
    { icon: <ShieldCheck size={32} strokeWidth={1.5} className="text-emerald-500" />, data: differenceData.point3, accentColor: 'emerald' },
    { icon: <Rocket size={32} strokeWidth={1.5} className="text-rose-500" />, data: differenceData.point4, accentColor: 'rose' },
  ];

  // --- NEW: Animation Variants ---

  // Parent container variant to orchestrate the stagger
  const gridContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15, // Time between each card's animation starts
      },
    },
  };

  // Variants for each individual card
  const cardVariants = {
    // A function to create variants for different directions
    from: (direction: 'left' | 'right' | 'top' | 'bottom') => ({
      hidden: {
        opacity: 0,
        x: direction === 'left' ? -40 : direction === 'right' ? 40 : 0,
        y: direction === 'top' ? -40 : direction === 'bottom' ? 40 : 0,
      },
      visible: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: {
          duration: 0.6,
          // CORRECTED: A valid cubic-bezier array or a standard string.
          ease: [0.25, 1, 0.5, 1], 
        },
      },
    }),
  };

  return (
    <section id={id} className="bg-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-12 md:mb-16"
        >
          {differenceData.headline}
        </motion.h2>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={gridContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* We will map over the points and apply a specific animation to each */}
          <motion.div variants={cardVariants.from('left')}>
            <ContentCard
              icon={<div className="p-3 rounded-lg border-2 border-black inline-block shadow-[3px_3px_0_0_black] bg-teal-100">{differencePoints[0].icon}</div>}
              title={differencePoints[0].data.title}
              text={differencePoints[0].data.text}
              className="bg-white border-black text-left h-full"
              hoverEffect="lift"
            />
          </motion.div>

          <motion.div variants={cardVariants.from('top')}>
            <ContentCard
              icon={<div className="p-3 rounded-lg border-2 border-black inline-block shadow-[3px_3px_0_0_black] bg-sky-100">{differencePoints[1].icon}</div>}
              title={differencePoints[1].data.title}
              text={differencePoints[1].data.text}
              className="bg-white border-black text-left h-full"
              hoverEffect="lift"
            />
          </motion.div>

          <motion.div variants={cardVariants.from('top')}>
            <ContentCard
              icon={<div className="p-3 rounded-lg border-2 border-black inline-block shadow-[3px_3px_0_0_black] bg-emerald-100">{differencePoints[2].icon}</div>}
              title={differencePoints[2].data.title}
              text={differencePoints[2].data.text}
              className="bg-white border-black text-left h-full"
              hoverEffect="lift"
            />
          </motion.div>

          <motion.div variants={cardVariants.from('right')}>
            <ContentCard
              icon={<div className="p-3 rounded-lg border-2 border-black inline-block shadow-[3px_3px_0_0_black] bg-rose-100">{differencePoints[3].icon}</div>}
              title={differencePoints[3].data.title}
              text={differencePoints[3].data.text}
              className="bg-white border-black text-left h-full"
              hoverEffect="lift"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

